/**
 * Single source of truth for monetary precision across the app.
 * All stored / displayed / submitted money values go through these helpers
 * so we never persist or render floating-point noise like 56.49999999999999.
 */

/**
 * Round a numeric value to 2 decimal places using banker-safe rounding.
 * Returns 0 for null/undefined/NaN/non-finite inputs.
 */
export const roundMoney = (value: any): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  // Add tiny epsilon to compensate for IEEE-754 representation, then round
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

/**
 * Format a monetary value as a fixed 2-decimal string ("56.50").
 * Suitable for display, persistence payloads, and currency outputs.
 */
export const formatMoney = (value: any): string => {
  return roundMoney(value).toFixed(2);
};

/**
 * Render with currency prefix ("$56.50"). Negative values get a leading minus.
 */
export const formatCurrency = (value: any, symbol: string = "$"): string => {
  const n = roundMoney(value);
  return n < 0 ? `-${symbol}${Math.abs(n).toFixed(2)}` : `${symbol}${n.toFixed(2)}`;
};

/**
 * Recursively walk an object/array and round any numeric value belonging to a
 * key in `fields`. Returns a new object — original is untouched.
 * Use before submitting payloads to ensure stored values are pre-rounded.
 */
export const roundMoneyFields = <T = any>(payload: T, fields: string[] = DEFAULT_MONEY_FIELDS): T => {
  if (payload === null || payload === undefined) return payload;
  if (Array.isArray(payload)) {
    return payload.map((item) => roundMoneyFields(item, fields)) as any;
  }
  if (typeof payload === "object") {
    const out: any = {};
    for (const key of Object.keys(payload as any)) {
      const val = (payload as any)[key];
      if (fields.includes(key) && (typeof val === "number" || (typeof val === "string" && val !== "" && Number.isFinite(Number(val))))) {
        out[key] = formatMoney(val);
      } else if (typeof val === "object" && val !== null) {
        out[key] = roundMoneyFields(val, fields);
      } else {
        out[key] = val;
      }
    }
    return out;
  }
  return payload;
};

/** Common monetary field names used across work-orders, invoices, payments. */
export const DEFAULT_MONEY_FIELDS = [
  "price",
  "tax",
  "taxAmount",
  "total",
  "unitPriceWithDiscount",
  "unitTotalWithDiscount",
  "amount",
  "amount_paid",
  "due_amount",
  "grandTotal",
  "subTotal",
  "subtotal",
  "quoteWorkOrderDiscount",
  "quoteWorkOrderSubTotal",
  "quoteWorkOrderSaleGrandTotal",
  "discount",
  "discount_amount",
  "value",
];
